import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Appstore, manageFirmSlice } from "../redux";
import { getFirmById } from "../Services/Firm";
import { showLoader, hideLoader } from "../redux/loaderActions";
import { selectedFirmNeedsDocumentReload } from "./selectedFirmDocumentReload";

const LEGACY_IFRAME_PATH_PREFIXES = [
  "/ManageFirm/Dashboard/",
  "/ManageFirm/GroupParticipants/",
  "/ManageFirm/Events/",
];

/** React routes that load legacy iframe; do not require `manageFirm` in Redux. */
export function isManageFirmLegacyIframePath(pathname: string): boolean {
  const p = pathname.toLowerCase();
  return LEGACY_IFRAME_PATH_PREFIXES.some((prefix) =>
    p.startsWith(prefix.toLowerCase())
  );
}

const UUID_SEGMENT =
  "([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})";

/**
 * Get the firm id from the URL to synchronize Redux in the ManageFirm layout.
 * Routes where the first UUID is not a firm (office, content, etc.) return undefined.
 */
export function resolveManageFirmIdForRedux(pathname: string): string | undefined {
  const p = pathname.replace(/\/+$/, "") || "/";
  const norm = p.toLowerCase();

  if (isManageFirmLegacyIframePath(p)) {
    return undefined;
  }

  if (norm === "/managefirm" || norm === "/managefirm/create" || norm === "/managefirm/create2") {
    return undefined;
  }
  if (norm === "/managefirm/dataadmin/create") {
    return undefined;
  }
  if (norm === "/managefirm/aopmanagement/update") {
    return undefined;
  }

  if (/\/content\/manage\//i.test(p)) {
    return undefined;
  }
  if (/\/content\/edit\//i.test(p)) {
    return undefined;
  }
  if (/\/content\/create$/i.test(p)) {
    return undefined;
  }

  if (/\/profile\/modify\/main\//i.test(p)) {
    return undefined;
  }
  if (/\/profile\/modify\/additional\//i.test(p)) {
    return undefined;
  }

  if (/\/aopmanagement\/professionals\//i.test(p)) {
    return undefined;
  }

  const notesEdit = new RegExp(
    `^/ManageFirm/GeneralTab/${UUID_SEGMENT}/Notes/Edit/`,
    "i"
  ).exec(p);
  if (notesEdit) {
    return notesEdit[1];
  }

  const phEdit = new RegExp(
    `^/ManageFirm/PracticeHeads/${UUID_SEGMENT}/Edit/`,
    "i"
  ).exec(p);
  if (phEdit) {
    return phEdit[1];
  }

  const uuidRe = new RegExp(UUID_SEGMENT, "gi");
  const allUuid = [...p.matchAll(uuidRe)].map((m) => m[1]);
  if (allUuid.length === 0) {
    return undefined;
  }

  return allUuid[0];
}

/**
 * If the URL includes a firmId and Redux does not have that firm document,
 * load the firm with the API and do `SetFirm`.
 * An office-row stub (main office membershipType "primary") is reloaded once.
 * Does not delete Redux in excluded routes; simply does nothing.
 */
export function useEnsureManageFirmLoaded(firmId: string | undefined) {
  const dispatch = useDispatch();
  const location = useLocation();
  const selectedFirm = useSelector(
    (s: Appstore) => s.manageFirm.selectedFirm
  );
  const selectedFirmId = selectedFirm?.id;
  const generationRef = useRef(0);
  const reloadedFirmIdsRef = useRef(new Set<string>());

  useLayoutEffect(() => {
    if (!firmId) {
      return;
    }
    if (isManageFirmLegacyIframePath(location.pathname)) {
      return;
    }
    if (selectedFirmId === firmId) {
      return;
    }
    if (selectedFirmId && selectedFirmId !== firmId) {
      dispatch(manageFirmSlice.actions.SetFirm(undefined));
    }
  }, [firmId, selectedFirmId, location.pathname, dispatch]);

  useEffect(() => {
    if (!firmId) {
      return;
    }
    if (isManageFirmLegacyIframePath(location.pathname)) {
      return;
    }
    if (!selectedFirmNeedsDocumentReload(selectedFirm, firmId)) {
      return;
    }
    if (reloadedFirmIdsRef.current.has(firmId)) {
      return;
    }

    const gen = ++generationRef.current;
    let cancelled = false;

    void (async () => {
      try {
        showLoader();
        const firm = await getFirmById(firmId);
        if (cancelled || gen !== generationRef.current) {
          return;
        }
        reloadedFirmIdsRef.current.add(firmId);
        if (firm?.id) {
          dispatch(manageFirmSlice.actions.SetFirm(firm));
        }
      } catch (e) {
        console.error("useEnsureManageFirmLoaded", e);
      } finally {
        if (!cancelled && gen === generationRef.current) {
          hideLoader();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [firmId, selectedFirm, location.pathname, dispatch]);
}
